import { TranslationKey } from '@Localization';
import { useInjection } from 'inversify-react';
import { ICurrentWorkspaceStore } from '../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { ACDLError, ForbiddenError, LockedUserError, UnauthorizedError } from '../../models/error.model';
import { IPagedResult } from '../../services/api.service';
import { IApiServiceImageUpload } from '../../services/api.service.imageUpload';
import { NotificationLevel, ToastNotification } from '../notifications/models/notification.model';
import { INotificationsService } from '../notifications/services/notifications.service';
import { IAttachment, IAttachmentBreadcrumb, IAttachmentsStore } from './attachments.store';

interface IAttachmentsPayload {
  attachments: IPagedResult<IAttachment>;
  ancestors: IAttachmentBreadcrumb[];
}

export interface IAttachmentsService {
  getAttachmentsAsync(parentId?: string, pageNumber?: number, pageSize?: number): Promise<void | ACDLError>;
  getAttachmentThumbnailUrl(attachmentId: string): string;
  getSelectedAttachments(allAttachments: boolean, attachmentIds: string[], parentId?: string): Promise<number | ACDLError>;
  addAttachmentFolderAsync(folderName: string, parentId?: string, pageNumber?: number): Promise<void | ACDLError>;
  deleteAttachmentsAsync(allAttachments: boolean, attachmentIds: string[], parentId?: string): Promise<void | ACDLError>;
  toggleDeleteAttachmentModal(show?: boolean): void;
  store: IAttachmentsStore;
}

export const useAttachmentsService = (): IAttachmentsService => {
  const store = useInjection(IAttachmentsStore.$);
  const apiServiceImageUpload = useInjection(IApiServiceImageUpload.$);
  const notificationsService = useInjection(INotificationsService.$);
  const currentWorkspaceStore = useInjection(ICurrentWorkspaceStore.$);

  const getAttachmentsAsync = async (parentId?: string, pageNumber?: number, pageSize?: number): Promise<void | ACDLError> => {
    const workspaceId = currentWorkspaceStore.currentWorkspace.id;
    if (!workspaceId) {
      return;
    }

    const paging = store.getPaging(workspaceId, parentId);
    const updatedPageNumber = pageNumber ?? paging.pageNumber;
    const updatedPageSize = pageSize ?? paging.pageSize;

    const params = {
      workspaceId,
      parentId: parentId ? parentId : null,
      pageNumber: updatedPageNumber,
      pageSize: updatedPageSize,
      orderBy: paging.orderBy,
      orderType: paging.orderType,
    };

    const result = await apiServiceImageUpload.getAsync<IAttachmentsPayload>('/Attachments', { params });

    if (await handleResponseError(result, 'get_attachments_failed')) {
      return;
    }

    const payload = result as IAttachmentsPayload;

    if (payload.attachments.totalCount > 0 && payload.attachments.pagesCount < payload.attachments.pageNumber) {
      await getAttachmentsAsync(parentId, payload.attachments.pagesCount - 1);
    } else {
      store.attachments.replace(payload.attachments.data);
      store.breadcrumbs.replace(payload.ancestors);

      store.updatePaging({
        ...paging,
        pageSize: updatedPageSize,
        pageNumber: payload.attachments.pageNumber,
        pagesCount: payload.attachments.pagesCount,
        totalCount: payload.attachments.totalCount,
      });
    }
  };

  const getAttachmentThumbnailUrl = (attachmentId: string): string => {
    return apiServiceImageUpload.getUrl(`/Attachments/thumbnail/${attachmentId}`);
  };

  const getSelectedAttachments = async (allAttachments: boolean, attachmentIds: string[], parentId?: string): Promise<number | ACDLError> => {
    const workspaceId = currentWorkspaceStore.currentWorkspace.id;
    const result = await apiServiceImageUpload.postAsync<number | ACDLError>('/Attachments/selectedAttachments', {
      workspaceId,
      allAttachments,
      parentId,
      attachmentIds,
    });

    return result;
  };

  const addAttachmentFolderAsync = async (name: string, parentId?: string): Promise<void | ACDLError> => {
    const workspaceId = currentWorkspaceStore.currentWorkspace.id;
    const result = await apiServiceImageUpload.postAsync<void | ACDLError>('/Attachments/addFolder', {
      workspaceId,
      parentId,
      name,
    });

    if (!(result instanceof ACDLError)) {
      await getAttachmentsAsync(parentId);
    }

    return result;
  };

  const deleteAttachmentsAsync = async (allAttachments: boolean, attachmentIds: string[], parentId?: string): Promise<void | ACDLError> => {
    const workspaceId = currentWorkspaceStore.currentWorkspace.id;
    store.isDeleteRequestInProgress = true;
    const result = await apiServiceImageUpload.postAsync<void | ACDLError>('/Attachments/deleteAttachments', {
      workspaceId,
      allAttachments,
      parentId,
      attachmentIds,
    });

    store.isDeleteRequestInProgress = false;

    if (await handleResponseError(result, 'delete_attachments_failed')) {
      return;
    }

    store.clearSelectedAttachments();
    store.showDeleteConfirmationModal = false;

    await getAttachmentsAsync(parentId);
  };

  const toggleDeleteAttachmentModal = (show?: boolean): void => {
    if (show !== undefined) {
      store.showDeleteConfirmationModal = show;
    } else {
      store.showDeleteConfirmationModal = !store.showDeleteConfirmationModal;
    }
  };

  const handleResponseError = async (result: unknown, notification: TranslationKey): Promise<boolean> => {
    if (result instanceof ACDLError) {
      if (result instanceof UnauthorizedError || result instanceof ForbiddenError || result instanceof LockedUserError) {
        throw result;
      }

      if (!result.isBadRequestWithCode(['USING_ATTACHMENTS_NOT_ALLOWED'])) {
        await notificationsService.push(new ToastNotification(NotificationLevel.ERROR, notification));
      }

      return true;
    }

    return false;
  };

  return {
    getAttachmentsAsync,
    getAttachmentThumbnailUrl,
    getSelectedAttachments,
    addAttachmentFolderAsync,
    deleteAttachmentsAsync,
    toggleDeleteAttachmentModal,
    store,
  };
};
